import { toPercentage, toSignificant } from '@/utilities/formatting';
import DataTable from '@components/DataTable';
import { SWS_ANALYSIS_MODEL } from '@/constants';

interface DividendDiscountModelProps {
  uniqueSymbol: string;
  reportingCurrencySymbol: string;
  companyShortName: string;
  dividendPerpetualGrowthRate: number;
  costOfEquity: number;
  DPS: number;
}

const DividendDiscountModel = ({
  uniqueSymbol,
  reportingCurrencySymbol,
  companyShortName,
  dividendPerpetualGrowthRate,
  costOfEquity,
  DPS,
}: DividendDiscountModelProps) => (
  <div>
    <p>
      {`The calculations below outline how an intrinsic value for ${companyShortName} is arrived at by discounting future dividends to their present value. This approach is used for companies that consistently pay out a meaningful portion of their earnings as dividends.`}
    </p>
    <p>
      If the firm does not pay the majority of its earnings out as a dividend
      this method will often arrive at a value significantly lower than the
      share price.
    </p>
    <p>
      <span>See our </span>
      <strong>
        <a
          href={`${SWS_ANALYSIS_MODEL}/MODEL.markdown#dividend-discount-model--how-is-this-calculated`}
          target="_blank"
          rel="noopener noreferrer nofollow"
        >
          documentation
        </a>
      </strong>
      <span> to learn about this calculation.</span>
    </p>
    <DataTable light>
      <caption>{`${uniqueSymbol} Gordon Growth Model`}</caption>
      <thead>
        <tr>
          <th />
          <th>Calculation</th>
          <th>Result</th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td>Value per share</td>
          <td>
            <span>{`= Expected dividends per share / (Discount Rate - Perpetual growth rate)`}</span>
            <br />
            <span>{`${reportingCurrencySymbol}${toSignificant(
              DPS,
              2
            )} / (${toPercentage(costOfEquity, 2)} - ${toPercentage(
              dividendPerpetualGrowthRate,
              2
            )})`}</span>
          </td>
          <td>
            {`${reportingCurrencySymbol}${toSignificant(
              DPS / (costOfEquity - dividendPerpetualGrowthRate),
              2
            )}`}
          </td>
        </tr>
      </tbody>
    </DataTable>
  </div>
);

export default DividendDiscountModel;
